import { Type } from '@angular/core';
import {
  property,
  subclass
} from '@arcgis/core/core/accessorSupport/decorators';
import Layer from '@arcgis/core/layers/Layer';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import {
  ThemeAwarePseudoLayer,
  ThemeAwarePseudoLayerProperties,
  ThemeColor,
  JepToolsLayerInterface,
  JepToolsMixin,
  queueStyleLayerVisibility
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { IFR, IFRRegexGroup } from '../../typings/ifr';
import { NavigationLayer } from '../navigation.layer';

export type NavigationPseudoLayerProperties =
  ThemeAwarePseudoLayerProperties<NavigationLayer> & {
    stylesRegex: IFRRegexGroup;
    order?: number;
    logger?: NGXLogger;
  };

export type NavigationPseudoLayerJepTools = {
  /**
   * Toggle or explicitly set visibility for the currently active IFR (or provided IFR)
   * Returns the resulting visibility state for consistency with other layers.
   */
  toggleVisibility: (visible?: boolean, ifr?: IFR) => boolean;
  /** Toggle IFR and update style visibility; returns the active IFR visibility state */
  toggleIFR: (ifr?: IFR) => boolean;
};

@subclass('NavigationPseudoLayer')
export class NavigationPseudoLayer extends JepToolsMixin<
  Type<ThemeAwarePseudoLayer<NavigationLayer>>,
  NavigationPseudoLayerJepTools
>(ThemeAwarePseudoLayer<NavigationLayer>, {
  toggleVisibility(
    this: NavigationPseudoLayer,
    visible?: boolean,
    ifr?: IFR
  ): boolean {
    if (visible !== undefined && visible !== this.visible) {
      this.visible = visible;
    } else if (visible === undefined) {
      // toggle when param omitted
      this.visible = !this.visible;
    }
    if (ifr) this.ifr = ifr;

    // Re-run themed styling via updateLayerStyles (hash inside guards repetition)
    this.updateLayerStyles(this.currentTheme);
    return this.visible;
  },

  toggleIFR(this: NavigationPseudoLayer, ifr?: IFR): boolean {
    this.handleVisibility(false, this.ifr);
    this.ifr = ifr ?? this.ifr;
    // Re-apply styling under new IFR, respecting user-configured visibility state
    this.handleVisibility(!!this.visible, this.ifr);

    return this.visible;
  }
}) {
  private _lastStyleHash?: string;
  private _initialVisibilityApplied = false;
  private _lastAppliedVisibility?: boolean;

  @property()
  override logger?: NGXLogger;

  constructor(props: any) {
    super(props);
    // Ensure an initial visibility pass once style layers & parent IFR/theme are ready.
    this.addHandles?.(
      reactiveUtils.when(
        () =>
          !!(
            (this as any).parentLayer?.currentStyleInfo?.style?.layers
              ?.length &&
            (this as any).parentLayer?.currentTheme &&
            this.stylesRegex
          ),
        () => {
          if (this._initialVisibilityApplied) return;
          this.updateLayerStyles(this.currentTheme);
          this._initialVisibilityApplied = true;
        }
      ),
      'theme'
    );

    // Watch for style layer availability if delayed
    this.addHandles?.(
      reactiveUtils.watch(
        () =>
          (this as any).parentLayer?.currentStyleInfo?.style?.layers?.length ||
          0,
        (len: number) => {
          if (len > 0 && !this._initialVisibilityApplied) {
            this.updateLayerStyles(this.currentTheme);
            this._initialVisibilityApplied = true;
          }
        },
        { initial: true }
      ),
      'theme'
    );

    // Propagate visible changes to child pseudo-layers so that any assignment to
    // this.visible (direct property set or via toggleVisibility) keeps child
    // visible state in sync — the ArcGIS property is the single source of truth
    this.addHandles?.(
      reactiveUtils.watch(
        () => this.visible,
        (visible) => {
          const childLayers = (this as any).layers as
            | { forEach: (cb: (child: unknown) => void) => void }
            | undefined;
          if (childLayers) {
            childLayers.forEach((child) => {
              if (
                child instanceof NavigationPseudoLayer &&
                child.visible !== visible
              ) {
                child.visible = visible;
              }
            });
          }
        }
      )
    );
  }

  /**
   * Handle visibility changes with theme awareness
   */
  private handleVisibility(visibility: boolean, ifr: IFR): boolean {
    const currentIFR = ifr;
    const theme = this.currentTheme; // From ThemeAwarePseudoLayer

    if (!this.stylesRegex?.[currentIFR]) {
      this.logger?.warn('[NavigationPseudoLayer] Invalid stylesRegex or IFR');
      return false;
    }

    // Apply visibility based on theme
    this.stylesRegex[currentIFR].forEach((regExpString) => {
      const currentThemeRegex = this.createThemedRegex(regExpString, theme);
      const oppositeThemeRegex = this.createThemedRegex(
        regExpString,
        theme === 'dark' ? 'light' : 'dark'
      );
      const layers = this.parentLayer.currentStyleInfo?.style.layers || [];
      let currentMatches = 0;
      let oppositeMatches = 0;
      layers.forEach((layer: Layer) => {
        if (currentThemeRegex.test(layer.id)) {
          currentMatches++;
          queueStyleLayerVisibility(
            this.parentLayer,
            layer.id,
            visibility ? 'visible' : 'none'
          );
        } else if (oppositeThemeRegex.test(layer.id)) {
          oppositeMatches++;
          queueStyleLayerVisibility(this.parentLayer, layer.id, 'none');
        }
      });
    });

    return !!visibility;
  }

  @property()
  stylesRegex!: IFRRegexGroup;

  @property()
  ifr: IFR = 'Low';

  private createThemedRegex(
    regExpString: string,
    theme: ThemeColor = 'light'
  ): RegExp {
    const themePrefix = theme === 'dark' ? 'Dark' : 'Light';
    return new RegExp(`^${themePrefix}/${regExpString}`);
  }

  /**
   * Central styling hook invoked by ThemeAwareMixin.applyTheme. Idempotent via hash.
   */
  protected updateLayerStyles(theme: ThemeColor): void {
    const layersReady =
      !!this.parentLayer?.currentStyleInfo?.style?.layers?.length;
    const effectiveVisible = !!this.visible;
    const hash = `${theme}|${this.ifr}|${effectiveVisible}`;
    const visibilityChanged = this._lastAppliedVisibility !== effectiveVisible;
    if (layersReady && hash === this._lastStyleHash && !visibilityChanged) {
      return;
    }
    if (!layersReady) {
      // Defer until style layers are present; do not set hash yet.
      return;
    }
    const prevHash = this._lastStyleHash;
    if (this.ifr) {
      this.handleVisibility(effectiveVisible, this.ifr);
      this._lastAppliedVisibility = effectiveVisible;
    }
    this._lastStyleHash = hash;
    this.logger?.trace(
      `[NavigationPseudoLayer ${this.id}] applied update prev=${prevHash} next=${this._lastStyleHash}`
    );
  }

  /** Log only (debug aid); styling handled in updateLayerStyles */
  override onThemeChange(oldTheme: ThemeColor, newTheme: ThemeColor): void {
    const prevHash = this._lastStyleHash;
    // Invalidate hash to force reapply when theme changes even if visibility & IFR same
    this._lastStyleHash = undefined;
    this.updateLayerStyles(newTheme);
    this.logger?.trace(
      `[NavigationPseudoLayer ${this.id}] theme ${oldTheme} -> ${newTheme} (restyled:${prevHash !== this._lastStyleHash})`
    );
  }
}

export function isNavigationPseudoLayer(
  layer: NavigationPseudoLayer
): layer is NavigationPseudoLayer &
  JepToolsLayerInterface<NavigationPseudoLayerJepTools> {
  return !!(
    layer &&
    typeof (layer as any).jepTools === 'object' &&
    typeof (layer as any).jepTools.toggleVisibility === 'function' &&
    typeof (layer as any).jepTools.toggleIFR === 'function'
  );
}
